import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_BADGE_DANGER_CLASSNAME,
  ADMIN_BADGE_INFO_CLASSNAME,
  ADMIN_BADGE_NEUTRAL_CLASSNAME,
  ADMIN_BADGE_PURPLE_CLASSNAME,
  ADMIN_BADGE_SUCCESS_CLASSNAME,
  ADMIN_BADGE_WARNING_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';

interface AllUser {
  id: string;
  email: string;
  status: 'waiting' | 'registered' | 'churned';
  plan: 'free' | 'pro' | 'editorial' | null;
  waitlistAt: string | null;
  registeredAt: string | null;
  brevoLastSync: string | null;
  preferredLanguageCode: string;
  disabled: boolean;
}

export const AllUsersAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [users, setUsers] = useState<AllUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [syncingEmails, setSyncingEmails] = useState<Set<string>>(new Set());

  const fetchUsers = useCallback(async () => {
    if ((!token && !masterKey) || !user?.admin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: '25',
      });
      const url = masterKey
        ? `${API_BASE}/api/admin/all-users?${params.toString()}&masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin/all-users?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching all users:', err);
      setError('Failed to load all users');
    } finally {
      setLoading(false);
    }
  }, [token, user, masterKey, currentPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClassName = (status: string): string => {
    switch (status) {
      case 'waiting':
        return ADMIN_BADGE_WARNING_CLASSNAME;
      case 'registered':
        return ADMIN_BADGE_SUCCESS_CLASSNAME;
      case 'churned':
        return ADMIN_BADGE_DANGER_CLASSNAME;
      default:
        return ADMIN_BADGE_NEUTRAL_CLASSNAME;
    }
  };

  const getPlanBadgeClassName = (plan: string | null): string => {
    switch (plan) {
      case 'pro':
        return ADMIN_BADGE_INFO_CLASSNAME;
      case 'editorial':
        return ADMIN_BADGE_PURPLE_CLASSNAME;
      case 'free':
        return ADMIN_BADGE_NEUTRAL_CLASSNAME;
      default:
        return ADMIN_BADGE_NEUTRAL_CLASSNAME;
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSyncBrevo = async (email: string) => {
    if (syncingEmails.has(email)) {
      return;
    }

    try {
      setSyncingEmails((prev) => new Set(prev).add(email));

      const url = masterKey
        ? `${API_BASE}/api/admin/all-users/sync-brevo?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin/all-users/sync-brevo`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to sync user' }));
        throw new Error(errorData.error || 'Failed to sync user');
      }

      await fetchUsers();
    } catch (err) {
      console.error('Error syncing user to Brevo:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync user to Brevo');
    } finally {
      setSyncingEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
    }
  };

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        {loading ? (
          <AdminLoadingState />
        ) : users.length === 0 ? (
          <AdminEmptyState>No users found</AdminEmptyState>
        ) : (
          <>
            <AdminTableWrap>
              <table className="admin-data-table min-w-[1300px]">
                <thead>
                  <tr>
                    <th>
                      {t('email')}
                    </th>
                    <th>
                      Status
                    </th>
                    <th>
                      {t('account_access_list_column')}
                    </th>
                    <th>
                      Plan
                    </th>
                    <th>
                      Waitlist At
                    </th>
                    <th>
                      Registered At
                    </th>
                    <th>
                      Brevo Last Sync
                    </th>
                    <th>
                      Idioma Preferido
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const statusBadgeClassName = getStatusBadgeClassName(u.status);
                    const planBadgeClassName = getPlanBadgeClassName(u.plan);
                    return (
                      <tr
                        key={u.id}
                      >
                        <td>
                          {u.status === 'registered' && u.registeredAt ? (
                            <Link
                              to={`/admin/users/${u.id}`}
                              className="admin-table-link"
                            >
                              {u.email}
                            </Link>
                          ) : (
                            u.email
                          )}
                        </td>
                        <td>
                          <span className={`${statusBadgeClassName} capitalize`}>
                            {u.status}
                          </span>
                        </td>
                        <td>
                          {u.status === 'registered' && u.disabled ? (
                            <span className={ADMIN_BADGE_DANGER_CLASSNAME}>
                              {t('account_access_blocked')}
                            </span>
                          ) : u.status === 'registered' ? (
                            <span className={ADMIN_BADGE_SUCCESS_CLASSNAME}>
                              {t('account_access_active')}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td>
                          {u.plan ? (
                            <span className={`${planBadgeClassName} capitalize`}>
                              {u.plan === 'pro' ? 'Pro' : u.plan === 'editorial' ? 'Editorial' : 'Free'}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="admin-table-cell-muted">
                          {formatDate(u.waitlistAt)}
                        </td>
                        <td className="admin-table-cell-muted">
                          {formatDate(u.registeredAt)}
                        </td>
                        <td className="admin-table-cell-muted">
                          <div className="flex items-center gap-2">
                            <span>{formatDate(u.brevoLastSync)}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleSyncBrevo(u.email)}
                              disabled={syncingEmails.has(u.email)}
                              className="h-auto whitespace-nowrap px-2 py-1 text-xs"
                            >
                              {syncingEmails.has(u.email) ? 'Syncing...' : 'Sync now'}
                            </Button>
                          </div>
                        </td>
                        <td>
                          {u.preferredLanguageCode || 'spa'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </AdminTableWrap>

            <AdminPaginationBar
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalCount={totalCount}
            />
          </>
        )}
      </div>
    </>
  );
};
